import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { listWidgets } from '../../../services/widgets.js';

/** Page sizes offered by the catalog's pagination control. */
export const WIDGET_LIBRARY_PAGE_SIZE_OPTIONS = [12, 24, 48];

const DEFAULT_PAGE_SIZE = 24;

const EMPTY_RESULT = Object.freeze({ widgets: [], total: 0, governedCount: 0 });

const hasActiveQueryFilters = (query = {}) => {
  const searchQuery = (query.searchKeyword ?? '').trim();
  if (searchQuery) return true;

  const filters = query.filters ?? {};
  return Object.entries(filters).some(([, value]) => {
    if (value == null || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  });
};

/**
 * Loads the widget catalog through `listWidgets` and exposes the four states
 * the page renders: loading, loaded, empty, no-results and error.
 */
export const useWidgetCatalog = ({ query } = {}) => {
  const [result, setResult] = useState(EMPTY_RESULT);
  const [error, setError] = useState(null);
  const [resolvedKey, setResolvedKey] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);

  const queryKey = useMemo(() => JSON.stringify(query ?? {}), [query]);
  const [currentPage, setCurrentPage] = useState(1);
  // Tracks the queryKey `currentPage` was last set for, so a filter/search
  // change resets to page 1 exactly once - never derived by comparing
  // queryKey strings, which can accidentally match a value visited earlier
  // (e.g. clearing filters back to the original empty query) and resurrect
  // a stale page number instead of resetting. Must be state, not a mutable
  // ref: React's "adjust state during render" pattern only restarts the
  // render correctly when the tracked previous value is itself state - a
  // ref mutated inline is written on the first pass and already looks
  // "up to date" if the render restarts, masking the very check it exists
  // to make.
  const [lastQueryKey, setLastQueryKey] = useState(queryKey);
  if (lastQueryKey !== queryKey) {
    setLastQueryKey(queryKey);
    if (currentPage !== 1) setCurrentPage(1);
  }

  const fetchIdRef = useRef(0);
  const parsedQuery = useMemo(() => JSON.parse(queryKey), [queryKey]);

  const hasActiveQuery = useMemo(
    () => hasActiveQueryFilters(parsedQuery),
    [parsedQuery],
  );

  const requestBody = useMemo(
    () => ({ page: currentPage, pageSize: itemsPerPage, ...parsedQuery }),
    [currentPage, itemsPerPage, parsedQuery],
  );

  const requestKey = useMemo(
    () => `${JSON.stringify(requestBody)}|${reloadToken}`,
    [requestBody, reloadToken],
  );

  const isLoading = resolvedKey !== requestKey;

  useEffect(() => {
    const fetchId = ++fetchIdRef.current;
    let mounted = true;

    listWidgets({
      page: requestBody.page,
      pageSize: requestBody.pageSize,
      orderBy: requestBody.orderBy,
      orderDirection: requestBody.orderDirection,
      filters: requestBody.filters,
      searchKeyword: requestBody.searchKeyword,
    })
      .then(({ widgets, total, governedCount }) => {
        if (!mounted || fetchId !== fetchIdRef.current) return;
        setResult({ widgets, total, governedCount });
        setError(null);
      })
      .catch((err) => {
        if (!mounted || fetchId !== fetchIdRef.current) return;
        setResult(EMPTY_RESULT);
        setError(err);
      })
      .finally(() => {
        if (mounted && fetchId === fetchIdRef.current) setResolvedKey(requestKey);
      });

    return () => {
      mounted = false;
    };
  }, [requestBody, requestKey]);

  const { widgets, total, governedCount } = result;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / (itemsPerPage || 1))),
    [total, itemsPerPage],
  );

  const rangeLabel = useMemo(() => {
    if (total === 0) return '0';
    const start = (currentPage - 1) * itemsPerPage + 1;
    return `${start}-${Math.min(currentPage * itemsPerPage, total)}`;
  }, [currentPage, itemsPerPage, total]);

  const isEmptyCatalog = !isLoading && !error && total === 0 && !hasActiveQuery;
  const isNoResults = !isLoading && !error && total === 0 && hasActiveQuery;

  const onPageChange = useCallback((next) => {
    if (!Number.isFinite(next) || next < 1) return;
    setCurrentPage(next);
  }, []);

  const onItemsPerPageChange = useCallback((next) => {
    setItemsPerPage(next);
    setCurrentPage(1);
  }, []);

  const refetch = useCallback(() => {
    setReloadToken((n) => n + 1);
  }, []);

  return useMemo(
    () => ({
      widgets,
      total,
      governedCount,
      currentPage,
      itemsPerPage,
      totalPages,
      rangeLabel,
      isLoading,
      isEmptyCatalog,
      isNoResults,
      error,
      onPageChange,
      onItemsPerPageChange,
      refetch,
    }),
    [
      widgets, total, governedCount, currentPage, itemsPerPage, totalPages,
      rangeLabel, isLoading, isEmptyCatalog, isNoResults, error,
      onPageChange, onItemsPerPageChange, refetch,
    ],
  );
};
